import { useId, useState } from 'react';
import { DialogShell } from '../../../components/DialogShell';
import type { VectorFeature } from '../../../data/model';
import {
  areaBufferError,
  prepareAreaSelection,
  type AreaOutput,
} from '../../../selection/createArea';
import { useAppStore } from '../../state/useAppStore';

export function CreateAreaDialog({
  features,
  onClose,
}: {
  readonly features: readonly VectorFeature[];
  readonly onClose: () => void;
}) {
  const [selection] = useState(() => prepareAreaSelection(features));
  const [output, setOutput] = useState<AreaOutput>('polygon');
  const [distance, setDistance] = useState(selection.family === 'polygon' ? '0' : '');
  const [unit, setUnit] = useState<'m' | 'km'>('m');
  const [notice, setNotice] = useState<string | null>(null);
  const createArea = useAppStore((state) => state.createAreaFromSelection);
  const target = useAppStore((state) =>
    state.project.annotationLayers.find(
      (layer) => layer.id === state.shell.activeAnnotationLayerId,
    ),
  );
  const titleId = useId();
  const errorId = useId();
  const metres = distance.trim() === '' ? NaN : Number(distance) * (unit === 'km' ? 1000 : 1);
  const bufferError =
    distance.trim() === ''
      ? selection.family === 'polygon'
        ? 'Enter a buffer distance.'
        : 'Enter a buffer distance greater than 0.'
      : areaBufferError(selection, metres);
  const error =
    selection.error ??
    (output === 'focus-area' && target?.annotations.some((item) => item.type === 'focus-area')
      ? 'This annotation layer already has a Focus Area. Select another annotation layer.'
      : null) ??
    bufferError;
  return (
    <DialogShell
      className="new-map-dialog create-area-dialog"
      titleId={titleId}
      {...(error ? { descriptionId: errorId } : {})}
      element="form"
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        if (error) return;
        const result = createArea(features, output, metres);
        if (result.ok) onClose();
        else setNotice(result.message);
      }}
    >
      <h2 id={titleId}>Create area from selection</h2>
      <p>
        {selection.count.toLocaleString()} {selection.family ?? 'vector'}{' '}
        {selection.count === 1 ? 'feature' : 'features'} selected
      </p>
      {selection.family === 'point' && selection.pointCount >= 3 ? (
        <p>Selected points will be enclosed by a convex boundary before buffering.</p>
      ) : null}
      <label className="attribute-query-control">
        <span>Output</span>
        <select
          aria-label="Output"
          value={output}
          onChange={(event) => {
            setOutput(event.target.value as AreaOutput);
            setNotice(null);
          }}
        >
          <option value="polygon">Polygon</option>
          <option value="focus-area">Focus Area</option>
        </select>
      </label>
      <div className="create-area-buffer">
        <label className="attribute-query-control">
          <span>Buffer distance</span>
          <input
            type="number"
            min="0"
            step="any"
            required
            value={distance}
            aria-invalid={bufferError && error === bufferError ? true : undefined}
            aria-describedby={bufferError && error === bufferError ? errorId : undefined}
            onChange={(event) => {
              setDistance(event.target.value);
              setNotice(null);
            }}
          />
        </label>
        <label className="attribute-query-control">
          <span>Units</span>
          <select
            aria-label="Units"
            value={unit}
            onChange={(event) => {
              const next = event.target.value as 'm' | 'km';
              if (distance.trim() && Number.isFinite(metres))
                setDistance(String(metres / (next === 'km' ? 1000 : 1)));
              setUnit(next);
              setNotice(null);
            }}
          >
            <option value="m">m</option>
            <option value="km">km</option>
          </select>
        </label>
      </div>
      <p>Annotation layer: {target?.name ?? 'New default annotation layer'}</p>
      {error ? (
        <p className="style-notice" id={errorId} role="status">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="style-notice" role="alert">
          {notice}
        </p>
      ) : null}
      <div className="new-map-dialog-actions">
        <button type="button" onClick={onClose}>
          Cancel
        </button>
        <button className="primary-action" disabled={Boolean(error)} type="submit">
          Create
        </button>
      </div>
    </DialogShell>
  );
}
